<template>
  <div class="station-event" v-if="event">
    <div class="d-flex align-baseline">
      <div class="headline font-weight-bold mr-4">
        <div class="d-flex align-center" v-if="event.installed_at && event.collected_at">
          <div class="mr-8">{{event.trap_no}}</div>
          <div>{{formatDate(event.installed_at)}}</div>
          <div class="mx-2">~</div>
          <div>{{formatDate(event.collected_at)}}</div>
        </div>
      </div>
      <div class="headline font-weight-bold flex">
        <div class="d-flex align-center" v-if="event.month">
          <div class="station-event__content align-center px-2">{{formatMonth(event.month)}}</div>
          <div class="ml-2">{{$t('month')}}</div>
          <div class="station-event__edit-button" v-if="isNotRecentEvent" >
            <v-menu
              offset-y
              v-if="downloadOptions.length > 0"
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  text
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>fa-download</v-icon>
                  <small>{{ $t('menu.download') }}</small>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  @click="download(downloadOption.type)"
                  v-for="downloadOption in downloadOptions"
                  :key="downloadOption.type"
                >
                  {{ downloadOption.label }}
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn
              text
              @click="download"
              v-else
            >
              <v-icon>fa-download</v-icon>
              <small>{{ $t('menu.download') }}</small>
            </v-btn>
            <v-btn text :disabled="!hasDetections" @click.native.stop="editing = true;" v-if="!event.updating">
              <v-icon>fa-edit</v-icon>
              <small>{{ $t('menu.edit') }}</small>
            </v-btn>
            <v-menu offset-y v-else-if="showTimeoutMenu">
              <template #activator="{ on, attrs }">
                <v-tooltip bottom>
                  <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <v-btn
                      text
                      v-bind="{ ...attrs, ...tooltipAttrs }"
                      v-on="{ ...on, ...tooltipOn }"
                      data-testid="edit-timeout-warning"
                    >
                      <v-badge color="warning" overlap bottom icon="mdi-alert">
                        <v-icon>fa-edit</v-icon>
                      </v-badge>
                      <small>{{ $t('menu.edit') }}</small>
                    </v-btn>
                  </template>
                  {{ $t('msg.timeout.detail') }}
                </v-tooltip>
              </template>
              <v-list>
                <v-list-item @click="retry" :disabled="!hasDetections" data-testid="retry">
                  <v-list-item-title>{{ $t('menu.retry') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="editing = true" data-testid="edit-again">
                  <v-list-item-title>{{ $t('menu.edit_again') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="confirmDismiss" data-testid="dismiss">
                  <v-list-item-title>{{ $t('menu.dismiss') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn text v-else disabled>
              <v-icon >fa-refresh</v-icon>
              <small>{{ $t('menu.updating') }}</small>
            </v-btn>
            <v-btn
              text
              v-if="!event.updating"
              @click.native.stop="$emit('deleteEvent')"
            >
              <v-icon>fa-trash</v-icon>
              <small>{{ $t('menu.delete') }}</small>
            </v-btn>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div class="pb-3">
        {{formatDateTime(event.upload_time)}}
      </div>
    </div>
    <div>
      <div>
        <station-event-processing v-if="event.processing" />
        <div class="d-flex flex-nowrap" v-else>
          <div class="station-event__main">
            <v-carousel v-model="selectedIndex" hide-delimiters touchless height="auto" :key="event.id">
              <v-carousel-item v-for="photo in photos" :key="photo.id" eager>
                <div class="d-flex justify-center w-100">
                  <div class="station-event__drawer">
                    <picked-insects-draw
                      :imageSrc="photo | imageSrc"
                      :detectionsUrl="makeDetectionsUrl(photo)"
                      :pickedDetectionType="pickedDetectionType"
                      :originalImageWidth='imageDimensions.width'
                      :originalImageHeight='imageDimensions.height'
                      :locomotion-scope="insectLocomotionScope"
                      :hideCanvas="viewOriginal"
                      @save="save"
                      ref="drawers"
                    />
                  </div>
                </div>
              </v-carousel-item>
            </v-carousel>
            <div class="station-event__selector">
              <dot-selector
                v-if="photos.length <= MAX_PHOTOS_FOR_DOT_SELECTOR"
                :value="selectedIndex"
                :size="photos.length"
                @change="selectPhoto"
              />
              <photo-page-selector
                v-else
                :value="selectedIndex"
                :max="photos.length - 1"
                @change="selectPhoto"
              />
            </div>
          </div>
          <div class="station-event__side">
            <div class="station-event__table-area">
              <div class="d-flex align-center justify-end px-1">
                <v-checkbox
                  class="ma-0 pa-0 flex-grow-1"
                  style="flex-grow: 1"
                  v-model="viewOriginal"
                  :label="$t('original')"
                  color="primary"
                  hide-details
                />
                <chart-type-select v-model="chartType" />
              </div>
              <insect-count-table
                v-if="chartTypeIs('table')"
                class="station-event__result-table"
                :insectCounts="event.insect_counts"
                :locomotion-scope="insectLocomotionScope"
                :showRadiobutton="!event.updating"
                :pickedDetectionType.sync="pickedDetectionType"
                showFocusButton
                @click:focus="handleFocus"
              />
              <insect-count-by-type-bar-chart
                :insectCounts="event.insect_counts"
                :locomotion-scope="insectLocomotionScope"
                v-if="chartTypeIs('bar')"
              />
              <top-5-insect-types-pie-chart
                :insectCounts="event.insect_counts"
                :locomotion-scope="insectLocomotionScope"
                v-if="chartTypeIs('pie')"
              />
            </div>
            <section data-testid="Metadata" class="pb-3 station-event__metadata">
              <ul>
                <li v-if="event.ip_address">
                  {{ $t('ip_address') }}: {{ event.ip_address }}
                </li>
                <li v-if="event.firmware_version">
                  {{ $t('firmware_version') }}: {{ event.firmware_version }}
                </li>
                <li v-if="event.serial_no">
                  {{ $t('serial_no') }}: {{ event.serial_no }}
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
      <insect-detections-editor
        :event="event"
        :locomotion-scope="insectLocomotionScope"
        :imageSrc="current.display"
        :detectionsUrl="makeDetectionsUrl(current)"
        :originalImageWidth='imageDimensions.width'
        :originalImageHeight='imageDimensions.height'
        canViewDetectionDetail
        v-model="editing"
        @updated="$emit('pollEvent')"
        v-if="isNotRecentEvent"
      >
        <template #header>
          <content-header class="w-100" :title="event.stationName" titleClass="title my-1">
            <!-- アイコン表示の為 fly_count を渡す-->
            <template #icon><icon type="fly_count" /></template>
            <template #subtitle>
              <div class="title details">
                <span>{{ timestamp.date }}</span>
                <span class="ml-3">
                  {{ timestamp.time }}
                </span>
              </div>
            </template>
          </content-header>
        </template>
      </insect-detections-editor>
      <ConfirmDialog
        v-model="dismissConfirmOpen"
        :message="$t('msg.dismiss.confirm')"
        @yes="executeDismiss"
        data-testid="dismiss-confirm-dialog"
      />
    </div>
  </div>
</template>

<i18n lang="yaml">
ja:
  title: '新着解析結果'
  count: '合計'
  month: '月分'
  original: 撮影画像を表示
  ip_address: ローカルIPアドレス
  firmware_version: ファームウェアのバージョン
  serial_no: S型
  menu:
    edit: '修正する'
    updating: '更新中'
    download: 'ダウンロード'
    delete: '削除'
    retry: '再依頼'
    edit_again: '編集し直す'
    dismiss: '無視する'
  msg:
    save:
      failed: 画像の保存に失敗しました。
    timeout:
      detail: '編集内容の反映に時間がかかっています。操作を選択できます'
    retry:
      success: '再依頼を受け付けました'
      failed: '再依頼に失敗しました'
    dismiss:
      confirm: '編集結果の反映は保証されません。「更新中」の表示のみ解除します。よろしいですか?'
      failed: '無視処理に失敗しました'

en:
  title: 'Recent'
  count: 'Total'
  month: ' '
  original: Show original photo
  ip_address: Local IP Address
  firmware_version: Firmware Version
  serial_no: S-type
  menu:
    edit: 'Edit'
    updating: 'Updating'
    download: 'Download'
    delete: 'Delete'
    retry: 'Retry'
    edit_again: 'Edit again'
    dismiss: 'Dismiss'
  msg:
    save:
      failed: Failed to save image.
    timeout:
      detail: 'The edit is taking longer than expected. Choose an action.'
    retry:
      success: 'Retry accepted.'
      failed: 'Retry failed.'
    dismiss:
      confirm: 'The edit result may not be reflected. This only dismisses the "Updating" display. Continue?'
      failed: 'Dismiss failed.'
</i18n>

<script>
import Notifications from '@/mixins/notifications';
import TrainingDataDownload from '@/mixins/trainingDataDownload';
import Features from '@/mixins/features';
import DateTimeFormatting from '@/mixins/dateTimeFormatting';

import ChartTypeSelect from '@/components/atoms/ChartTypeSelect';
import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import InsectCountByTypeBarChart from '@/components/organisms/InsectCountByTypeBarChart';
import Top5InsectTypesPieChart from '@/components/organisms/Top5InsectTypesPieChart';
import ContentHeader from '@/components/ContentHeader';
import Icon from '@/components/Icon';
import InsectCountTable from '@/components/InsectCountTable';
import InsectDetectionsEditor from '@/components/InsectDetectionsEditor';
import PickedInsectsDraw from '@/components/PickedInsectsDraw';
import { ZoomProvider } from '@/components/Zoom';

import { calculateLocomotionScope } from '@/libs/insectTypes';
import { isEditingTimedOut } from '@/libs/isEditingTimedOut';

import DotSelector from './DotSelector';
import PhotoPageSelector from './PhotoPageSelector';
import StationEventProcessing from './StationEventProcessing';

const ORIGINAL_IMAGE_WIDTH = 50248;
const ORIGINAL_IMAGE_HEIGHT = 5472;
const MAX_PHOTOS_FOR_DOT_SELECTOR = 5;

export default {
  name: 'station-v2-event',
  mixins: [
    Notifications,
    TrainingDataDownload,
    Features,
    ZoomProvider,
    DateTimeFormatting,
  ],
  components: {
    ChartTypeSelect,
    ConfirmDialog,
    ContentHeader,
    DotSelector,
    PhotoPageSelector,
    Icon,
    InsectCountByTypeBarChart,
    InsectCountTable,
    InsectDetectionsEditor,
    StationEventProcessing,
    PickedInsectsDraw,
    Top5InsectTypesPieChart,
  },
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  computed: {
    insectLocomotionScope() {
      return calculateLocomotionScope(this.event, this.featureIsActive);
    },
    imageDimensions() {
      const defaultDimensions = {
        width: ORIGINAL_IMAGE_WIDTH,
        height: ORIGINAL_IMAGE_HEIGHT,
      };

      // フィーチャートグル無効時は固定値
      if (!this.featureIsActive('s_type_scanner_upload')) {
        return defaultDimensions;
      }

      return this._.get(
        this.event,
        `photo_dimensions.${this.current?.key}`,
        defaultDimensions,
      );
    },
    current() {
      return this._.get(this.photos, `[${this.selectedIndex}]`);
    },
    photos() {
      const { tab, ...photos } = this.event.attachments;
      const withKey = this._.map(photos, (value, key) => ({ key, ...value }));

      return [
        { key: 'tab', ...tab },
        ...withKey.sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true })),
      ];
    },
    isNotRecentEvent() {
      return !this.$route.path.match(/recent/);
    },
    hasDetections() {
      return this.current.key !== 'tab';
    },
    timestamp() {
      return {
        date: this.formatDate(this.event.upload_time),
        time: this.formatTime(this.event.upload_time),
      };
    },
    showTimeoutMenu() {
      return isEditingTimedOut(this.event, Date.now());
    },
  },
  data() {
    return {
      selectedIndex: 1,
      chartType: 'table',
      pickedDetectionType: 'all',
      editing: false,
      viewOriginal: false,
      dismissConfirmOpen: false,
      ORIGINAL_IMAGE_WIDTH,
      ORIGINAL_IMAGE_HEIGHT,
      MAX_PHOTOS_FOR_DOT_SELECTOR,
    };
  },
  inject: ['api', 'downloadAttachment'],
  methods: {
    handleFocus() {
      const drawer = this.$refs.drawers[this.selectedIndex];
      drawer.focusNext();
    },
    download: async function (type) {
      switch (type) {
        case 'trainingData': {
          this.downloadAttachment(this.event.id, this.current.key);
          break;
        }
        default: {
          const drawer = this.$refs.drawers[this.selectedIndex];
          this.save(drawer);
        }
      }
    },
    save: async function (canvas) {
      try {
        await canvas.export('download.png');
      } catch (error) {
        this.notifyError('msg.save.failed');
      }
    },
    selectPhoto(photoIndex) {
      this.selectedIndex = photoIndex;
    },
    chartTypeIs(view) {
      return this.chartType === view;
    },
    makeDetectionsUrl(photo) {
      if (photo.key !== 'tab') {
        return `/api/v2/events/${this.event.id}/insect_detections/${photo.key}`;
      }
      return null;
    },
    async retry() {
      const originalStartedAt = this.event.updating_started_at;
      this.$emit('pollEvent', { updating_started_at: Math.floor(Date.now() / 1000) });
      try {
        const attachment = this.event.updating_attachment;
        const data = await this.api.fetchInsectDetectionResult(this.event.id, attachment);
        await this.api.updateInsectDetectionResult(this.event.id, attachment, data.positions);
        this.notifySuccess('msg.retry.success');
      } catch (error) {
        this.$emit('pollEvent', { updating_started_at: originalStartedAt });
        this.notifyError('msg.retry.failed');
      }
    },
    confirmDismiss() {
      this.dismissConfirmOpen = true;
    },
    async executeDismiss() {
      try {
        await this.api.dismissInsectDetectionUpdate(this.event.id);
        this.$emit('pollEvent', {
          updating: false,
          updating_started_at: null,
          updating_attachment: null,
        });
      } catch (error) {
        this.notifyError('msg.dismiss.failed');
      } finally {
        this.dismissConfirmOpen = false;
      }
    },
    // public
    resetView() {
      this.chartType = 'table';
    },
  },
  filters: {
    imageSrc(photo) {
      if (photo.key === 'tab') {
        return photo.original;
      }
      return photo.display;
    },
  },
  watch: {
    event: function (value, prev) {
      if (value.id !== prev.id) {
        this.selectedIndex = 1;
        this.pickedDetectionType = 'all';
        this.resetZoom();
      }
    },
    'event.updating': function (value, prev) {
      if (prev && !value) {
        this.$refs.drawers.forEach((drawer) => {
          drawer.refetch();
        });
      }
    },
    'event.updating_attachment': {
      immediate: true,
      handler(newAttachment) {
        if (!newAttachment || !isEditingTimedOut(this.event, Date.now())) return;
        const index = this.photos.findIndex(p => p.key === newAttachment);
        if (index >= 0) this.selectedIndex = index;
      },
    },
    selectedIndex() {
      this.resetZoom();
    },
  },
};
</script>

<style scoped lang="sass">
.station-event
  &__metadata
    font-size: 80%
    ul
      padding-left: 0px
      list-style: none

  &__drawer
    height: 65vh
    display: flex
    max-width: 100%
    overflow-x: auto
    overflow-y: hidden

  &__selector
    display: flex
    justify-content: center
    margin-top: 0.5rem

  &__main
    flex-grow: 1
    max-width: calc(100% - 450px)
    margin-right: 10px

    // To trigger resize event
    .v-window-item.active
      padding-right: 1px

  &__side
    width: 450px
    min-width: 450px
    display: flex
    flex-direction: column

  &__table-area
    min-height: 500px
    height: 65vh
    display: flex
    flex-direction: column
    border: 2px solid #ddd

  &__edit-button
    margin: 0 0 0 auto

    .v-btn
      margin: 0
      height: auto
      min-width: 64px

    ::v-deep .v-btn__content
      height: auto
      flex-direction: column

    .v-icon
      color: #0068b6

    small
      line-height: 2em

  &__content
    border: 2px solid #ccc
    background-color: #fff

  .loading-image, .picked-insects-draw
    height: 100%
    min-height: 100px
    width: 100%
    object-fit: contain

  &__result-table
    overflow: hidden
    flex-grow: 1

    ::v-deep .v-data-table
      height: 100%

      &__wrapper
        height: 100%
        overflow-y: auto

        td
          background-color: #fff

  ::v-deep .dot-selector
    &__item:first-child::before
      content: '\f02b'
      font: normal normal normal 14px/1 FontAwesome

.details
  padding-left: 3rem
  padding-right: 3rem

::v-deep .v-window__prev,
::v-deep .v-window__next
  z-index: 12

.col
  padding: 0
</style>
